type FastApiValidationItem = { msg?: string };

type FastApiErrorBody = {
  message?: string;
  detail?: string | FastApiValidationItem[];
};

type HttpErrorLike = {
  status?: unknown;
  message?: unknown;
};

/** Matches `throwHttpError` messages such as `HTTP 400:` or `HTTP 422: Unprocessable Entity`. */
const HTTP_STATUS_MESSAGE = /^HTTP (\d{3})\b/;

const isHttpStatus = (status: number): boolean =>
  Number.isInteger(status) && status >= 100 && status <= 599;

/**
 * Reads the HTTP status from a `throwHttpError` / `fetchOnboardingJson` error.
 * Prefers the attached `.status` field, then an `HTTP <code>` message prefix.
 */
export const getHttpErrorStatus = (error: unknown): number | undefined => {
  if (typeof error === 'string') {
    const match = HTTP_STATUS_MESSAGE.exec(error);
    return match ? Number(match[1]) : undefined;
  }
  if (error == null || typeof error !== 'object') {
    return undefined;
  }

  const status = (error as HttpErrorLike).status;
  if (typeof status === 'number' && isHttpStatus(status)) {
    return status;
  }

  const message =
    error instanceof Error
      ? error.message
      : typeof (error as HttpErrorLike).message === 'string'
        ? (error as HttpErrorLike).message
        : undefined;
  if (typeof message === 'string') {
    const match = HTTP_STATUS_MESSAGE.exec(message);
    if (match) {
      return Number(match[1]);
    }
  }

  return undefined;
};

/**
 * True for failed API responses (`customFetch` / `throwHttpError`).
 * 4xx and 5xx are already logged on the API and are not UI crashes.
 */
export const isApiHttpError = (error: unknown): boolean => {
  const status = getHttpErrorStatus(error);
  return status !== undefined && status >= 400;
};

/**
 * Extract a human-readable message from API error bodies (e.g. FastAPI 422 `detail: [...]`
 * with `msg` on each item). Falls back when the shape is unknown.
 */
export const getApiErrorMessage = (data: unknown, fallback: string): string => {
  if (data == null || typeof data !== 'object') {
    return fallback;
  }
  const b = data as FastApiErrorBody;
  if (typeof b.message === 'string' && b.message) {
    return b.message;
  }
  if (Array.isArray(b.detail)) {
    const parts: string[] = [];
    for (const item of b.detail) {
      if (item && typeof item === 'object' && typeof item.msg === 'string') {
        parts.push(item.msg);
      } else if (typeof item === 'string') {
        parts.push(item);
      }
    }
    if (parts.length > 0) {
      return parts.join(' ');
    }
  }
  if (typeof b.detail === 'string' && b.detail) {
    return b.detail;
  }
  return fallback;
};

/**
 * Extract a human-readable message from a thrown API error. Errors surfaced by
 * `throwHttpError` in `custom-fetch.ts` carry the FastAPI response body on
 * `err.data`; this unwraps that first (via {@link getApiErrorMessage}) so
 * validation `detail` shows up in the UI instead of "HTTP 400: Bad Request".
 * Falls back to `err.message` when there's no body, and then to `fallback`.
 */
export const getApiErrorMessageFromError = (
  err: unknown,
  fallback = 'Something went wrong.'
): string => {
  if (err instanceof Error) {
    const data = (err as { data?: unknown }).data;
    const errFallback = err.message || fallback;
    if (data != null) {
      return getApiErrorMessage(data, errFallback);
    }
    return errFallback;
  }
  if (typeof err === 'string' && err) {
    return err;
  }
  return fallback;
};
